import { useContext, useEffect, useState } from "react";
import {
	Container,
	CustomButton,
	Dot,
	ExpandedText,
	StyledButtonGroup
} from "./styles";
import { configData, parseData } from "./helpers";
import { ISectionMenuItem } from "components/PageTopNav";
import { debounce } from "lodash";
import { useAppDispatch, useAppSelector, useImagePath } from "hooks";
import { selectPopUpManagerState, selectStickyDIPState } from "redux/slices";
import { useGetFeatureTrackerQuery } from "redux/apis";
import {
	useEnvContext,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import { FeaturesFlags } from "helpers";
import { EndlessOfferResponse } from "interfaces/endless-offer";
import { WaterfallResponse } from "interfaces/waterfall-sale";
import useNewDay from "hooks/useNewDay";
import {
	onDisplayLimitedDealsDot,
	onScrollToShopSection,
	postUserTrackerValue,
	selectCommonState
} from "redux/slices/common";
import { tutorialAssets } from "assets/cdn";
import { Shine } from "components/PopUpManager/lib/Tutorial/styles";
import { CircleIcon } from "utils/svgIcons";
import { ShopContext } from "pages/StoreFront";

export const SectionMenu = ({ items }: { items: ISectionMenuItem[] }) => {
	const { getImage } = useImagePath("cdn");
	const shine = getImage(tutorialAssets.shine);
	const { newDay } = useNewDay();
	const dispatch = useAppDispatch();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { sectionMenuData } = useContext(ShopContext);
	const { authToken, userTracker } = useWebStoreContext();
	const { variant } = useAppSelector(selectPopUpManagerState);
	const { data: stickyData } = useAppSelector(selectStickyDIPState);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { displayLimitedDealsDot, scrollToSection } =
		useAppSelector(selectCommonState);

	const HEADER_OFFSET = 145;

	const itemsFiltered = items.filter((item) => item.sectionRef?.current);

	const [selected, setSelected] = useState(0);

	const isWaterfallSaleActive = () => {
		return (
			!!authToken?.value && isFeatureFlagEnabled(FeaturesFlags.WATERFALL_SALES)
		);
	};

	const isEndlessOfferActive = () => {
		return (
			!!authToken?.value && isFeatureFlagEnabled(FeaturesFlags.ENDLESS_OFFER)
		);
	};

	const isStickyDipActive = () => {
		return isFeatureFlagEnabled(FeaturesFlags.STICKY_DIPS);
	};

	const {
		data: waterfallData,
		refetch: refetchWaterfall
	}: { data?: WaterfallResponse; refetch: () => void } =
		useGetFeatureTrackerQuery(
			{
				FeatureType: "waterfall-sale",
				isAuthenticated: !!authToken?.value
			},
			{ skip: !isWaterfallSaleActive() }
		);

	const {
		data: endlessOfferData,
		refetch: refetchEndlessOffer
	}: { data?: EndlessOfferResponse; refetch: () => void } =
		useGetFeatureTrackerQuery(
			{
				FeatureType: "endless-offer",
				isAuthenticated: !!authToken?.value
			},
			{ skip: !isEndlessOfferActive() }
		);

	const getConfig = (name: string) => {
		return configData.find((item) => item.name === name.toLowerCase());
	};

	const handleExpand = (index: number) => {
		if (variant === "tutorial") {
			return;
		}
		const sectionElement = itemsFiltered[index].sectionRef?.current;
		if (sectionElement) {
			const rect = sectionElement.getBoundingClientRect();
			const offset = rect.top + window.scrollY - HEADER_OFFSET;
			window.scrollTo({ top: offset, behavior: "smooth" });

			reportEvent("navbar_click", {
				source: "store_page",
				navbar_type: itemsFiltered[index].text
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "navbar_click",
				event_type: "navbar",
				action: "navbar_click",
				source: "store_page",
				type: itemsFiltered[index].text
			});
		}
	};

	const handleScroll = () => {
		const scrollPosition = window.scrollY;
		const viewportTop = scrollPosition + HEADER_OFFSET;
		const viewportBottom = scrollPosition + window.innerHeight;

		const sectionElements = itemsFiltered
			.map((item, index) => {
				const sectionElement = item.sectionRef?.current;
				if (!sectionElement) {
					return null;
				}
				const rect = sectionElement.getBoundingClientRect();
				const top = rect.top + scrollPosition;
				const bottom = rect.bottom + scrollPosition;
				return { index, top, bottom };
			})
			.filter(
				(section): section is { index: number; top: number; bottom: number } =>
					section !== null
			);

		const activeSection = sectionElements.reduce<{
			index: number;
			visibleHeight: number;
		} | null>((prev, current) => {
			const visibleTop = Math.max(current.top, viewportTop);
			const visibleBottom = Math.min(current.bottom, viewportBottom);
			const visibleHeight = Math.max(0, visibleBottom - visibleTop);

			if (visibleHeight <= 0) {
				return prev;
			}

			if (!prev || visibleHeight > prev.visibleHeight) {
				return { index: current.index, visibleHeight };
			}

			return prev;
		}, null);

		if (activeSection && activeSection.index !== selected) {
			setSelected(activeSection.index);
		}
	};

	const debouncedHandleScroll = debounce(handleScroll, 100);

	const sawCurrentLimitedDealsSection = () => {
		if (userTracker) {
			const values = userTracker.metadata?.lastSeenLimitedDealSectionV2
				? parseData(userTracker.metadata.lastSeenLimitedDealSectionV2)
				: "";
			const updatedValues = { ...values };
			if (isWaterfallSaleActive() && waterfallData?.sessionTracker?.id) {
				updatedValues.waterfall = waterfallData.sessionTracker.id;
			}
			if (isEndlessOfferActive() && endlessOfferData?.tracker?.eventId) {
				updatedValues.endlessSale = endlessOfferData.tracker.eventId;
			}
			if (isStickyDipActive() && stickyData) {
				updatedValues.stickyDip = Object.keys(stickyData);
			}

			dispatch(
				postUserTrackerValue({
					key: "lastSeenLimitedDealSectionV2",
					value: JSON.stringify(updatedValues)
				})
			);
		}
		dispatch(onDisplayLimitedDealsDot(false));
	};

	useEffect(() => {
		if (
			scrollToSection &&
			window.location.pathname === "/shop" &&
			itemsFiltered.length > 0 &&
			!!itemsFiltered.find((item) => item.name === "featured") &&
			sectionMenuData
		) {
			const sectionIndex = itemsFiltered.findIndex(
				(item) => item.sectionRef?.current.id === scrollToSection
			);
			if (sectionIndex !== -1) {
				handleExpand(sectionIndex);
			}
			dispatch(onScrollToShopSection(undefined));
		}
	}, [scrollToSection, window.location, itemsFiltered, sectionMenuData]);

	useEffect(() => {
		window.addEventListener("scroll", debouncedHandleScroll);
		return () => {
			window.removeEventListener("scroll", debouncedHandleScroll);
		};
	}, [itemsFiltered]);

	useEffect(() => {
		reportEvent("navbar_scroll", {
			source: "store_page",
			navbar_type: itemsFiltered?.[selected]?.text
		});
	}, [selected]);

	useEffect(() => {
		if (window.location.pathname !== "/shop") {
			return;
		}
		setTimeout(() => {
			const parsedMetadata = userTracker?.metadata?.lastSeenLimitedDealSectionV2
				? parseData(userTracker.metadata.lastSeenLimitedDealSectionV2)
				: "";
			if (isWaterfallSaleActive() && waterfallData?.sessionTracker) {
				dispatch(
					onDisplayLimitedDealsDot(
						waterfallData?.sessionTracker?.id !== parsedMetadata?.waterfall
					)
				);
				return;
			}
			if (isEndlessOfferActive() && endlessOfferData?.tracker) {
				dispatch(
					onDisplayLimitedDealsDot(
						endlessOfferData?.tracker.eventId !== parsedMetadata?.endlessSale
					)
				);
				return;
			}
			if (isStickyDipActive() && stickyData) {
				const arraysHaveSameElements = (
					arr1: string[],
					arr2: string[]
				): boolean => {
					if (arr1.length > arr2.length) {
						return false;
					}
					const filteredArr2 =
						arr1.length < arr2.length
							? arr2.filter((val) => arr1.includes(val))
							: null;
					const sortedArr1 = [...arr1].sort();
					const sortedArr2 = [...(filteredArr2 ?? arr2)].sort();

					return sortedArr1.every((val, index) => val === sortedArr2[index]);
				};
				dispatch(
					onDisplayLimitedDealsDot(
						!arraysHaveSameElements(
							Object.keys(stickyData),
							parsedMetadata?.stickyDip ?? []
						)
					)
				);
			}
		}, 3000);
	}, [
		userTracker?.metadata,
		waterfallData,
		endlessOfferData,
		stickyData,
		window.location
	]);

	useEffect(() => {
		if (newDay) {
			refetchWaterfall();
			refetchEndlessOffer();
		}
	}, [newDay]);

	useEffect(() => {
		if (
			itemsFiltered?.[selected]?.text === "Limited Deals" &&
			window.location.pathname === "/shop" &&
			displayLimitedDealsDot
		) {
			setTimeout(() => {
				sawCurrentLimitedDealsSection();
			}, 1000);
		}
	}, [itemsFiltered, selected, displayLimitedDealsDot, window.location]);

	if (!itemsFiltered.length) {
		return null;
	}

	return (
		<Container>
			<StyledButtonGroup variant="contained" color="primary">
				{itemsFiltered.map((item, index) => {
					const config = getConfig(item.name);

					return (
						<div key={index}>
							{item.name === "wilds" && (
								<Shine id="sub-menu-shine-effect" src={shine} width={132} />
							)}
							<CustomButton
								id={`expand-button-${item.name}`}
								className={selected === index ? "expanded" : ""}
								onClick={() => handleExpand(index)}
							>
								{config?.icon ? (
									<config.icon
										width={"1.83em"}
										height={"1.83em"}
										fill={selected === index ? "#479FBA" : "white"}
									/>
								) : (
									<CircleIcon
										width={"1.83em"}
										height={"1.83em"}
										fill={selected === index ? "#479FBA" : "white"}
									/>
								)}
								<ExpandedText className={selected === index ? "expanded" : ""}>
									{item.text}
								</ExpandedText>
								{selected !== index &&
									item.text === "Limited Deals" &&
									displayLimitedDealsDot && <Dot />}
							</CustomButton>
						</div>
					);
				})}
			</StyledButtonGroup>
		</Container>
	);
};
